// Vocabulary: readers see this feature as "owner reviews"; code, routes, DealHunt paths and
// analytics events keep the historical "owner note" name on purpose — do not rename them.
// The PUBLIC card for one owner's note on the PDP wall. Not to be confused with `OwnNoteCard`,
// the author's card for their own note.
import clsx from 'clsx'
import styles from './note-card.module.css'
import OwnerHint from './OwnerHint'
import { DepartureChips, ownershipLine } from './NoteAnswers'
import { buyAgainMark, noteAnchorId } from 'lib/owner-notes/band-copy'
import { PHOTO_HINT, PSEUDONYM_HINT, PUBLIC_VIEWS_HINT } from 'lib/owner-notes/hints'
import { publicReadersLabel } from 'lib/owner-notes/views'
import type { OwnerNotePublic, OwnerQuestionSetDef } from 'lib/dealhunt/types'
import type { CommunityHelpfulAnswer } from 'lib/community/types'
import NoteReport, { type NoteReportHandler } from './NoteReport'
import ReviewEngagementRow from './ReviewEngagementRow'
import ReviewPhotos from './ReviewPhotos'
import MemberName from 'components/community/MemberName'
import TierAvatar from 'components/community/TierAvatar'

/** The wall's "Helpful · Reply →" row for one card (`ReviewEngagementRow`, full variant). */
export interface OwnerNoteEngagement {
  /** The review's thread (`communityThreadHref`); null → Helpful alone. */
  thread: string | null
  /** The wall's batched read of the reader's votes (`useHelpfulMine`), for this note. */
  known?: CommunityHelpfulAnswer
  /** The wall's memory of DealHunt's Helpful answers (a re-rendered card keeps the vote). */
  memory?: Map<string, CommunityHelpfulAnswer>
  onReply?: () => void
}

interface OwnerNoteCardProps {
  note: OwnerNotePublic
  /** Labels for the answer ids; null before the question set loads (ids render raw then). */
  questions: OwnerQuestionSetDef | null
  /** The note's all-time readers, when this reader may see it (#155): the section passes
   *  `note.views_total` for a signed-in reader and null otherwise. Under the first milestone
   *  the card shows nothing. Never set on the server pass — the count is not in the HTML. */
  views?: number | null
  /** Report handler owned by the section (it mints a device first when needed, with the
   *  card's own bot check). Absent → no report control (the server-rendered pass has none;
   *  it hydrates in). */
  onReport?: NoteReportHandler
  /** For the photos' alt text ("<product> — owner photo"). */
  productName?: string | null
  /** The wall passes it; absent (the guide's example card) → no Helpful, no Reply: a made-up
   *  review has no vote and no thread. */
  engagement?: OwnerNoteEngagement
}

/**
 * One published owner note (design 2a, 2026-09-22): one meta line — pseudonym, badge word,
 * the ownership line, and for a signed-in reader the note's reader count (#155) — with the
 * buy-again mark on the right; the sentence; then the departure chips. No country, no exact
 * month count: the meta carries verification, an owned-duration band, current use and the
 * post month, and nothing else.
 */
const OwnerNoteCard: React.VFC<OwnerNoteCardProps> = ({
  note,
  questions,
  views = null,
  onReport,
  productName = null,
  engagement,
}) => {
  const { answers } = note
  const verified = note.photo_status === 'verified'
  const readers = publicReadersLabel(views)

  return (
    // `id`: "Read the full owner review" on a review post's product card lands here (`#note-{id}`, ticket
    // §4.4); `top_notes` are the first three of this default order, so the target is always in
    // the server-rendered first page.
    <article className={styles.note} id={noteAnchorId(note.id)} data-note-id={note.id}>
      <header className={styles.metaRow}>
        <p className={styles.metaLine}>
          {/* DealHunt #218: the author as the community names them — the shown name and photo
              when the member shows them, `@pseudonym` alone otherwise. */}
          <TierAvatar
            handle={note.handle}
            tier={null}
            size="xs"
            src={note.avatar_url}
            name={note.display_name}
            className={styles.authorAvatar}
          />
          <OwnerHint hint={PSEUDONYM_HINT}>
            <span className={styles.handle}>
              {note.display_name ? (
                <MemberName name={note.display_name} handle={note.handle} />
              ) : (
                `@${note.handle}`
              )}
            </span>
          </OwnerHint>
          {' · '}
          <OwnerHint hint={PHOTO_HINT[note.photo_status]}>
            <span className={clsx(styles.badge, { [styles.badgeVerified]: verified })}>
              {note.photo_label}
            </span>
          </OwnerHint>
          {' · '}
          <span className={styles.ownershipLine}>
            {ownershipLine(answers, questions, note.published_at)}
          </span>
          {readers && (
            // A figure, not a ranking: muted, the meta line's last item (#155).
            <>
              {' · '}
              <OwnerHint hint={PUBLIC_VIEWS_HINT} className={styles.viewsPublic}>
                <span>{readers}</span>
              </OwnerHint>
            </>
          )}
        </p>
        {typeof answers.buy_again === 'boolean' && (
          <span className={clsx(styles.buyAgain, answers.buy_again ? styles.buyYes : styles.buyNo)}>
            {buyAgainMark(answers.buy_again)}
          </span>
        )}
      </header>
      <p className={styles.noteText}>{note.note}</p>
      <ReviewPhotos note={note} productName={productName} />
      <DepartureChips answers={answers} questions={questions} />
      {engagement && (
        <ReviewEngagementRow
          noteId={note.id}
          handle={note.handle}
          helpfulCount={note.helpful_count}
          helpfulKnown={engagement.known}
          helpfulMemory={engagement.memory}
          thread={engagement.thread}
          onReply={engagement.onReply}
        />
      )}
      {onReport && <NoteReport noteId={note.id} onReport={onReport} />}
    </article>
  )
}

export default OwnerNoteCard
